<template>
    <div class="ui-page voting-scenarios">
        <ui-page-header title="Cenários de votação" subtitle="Simulações de composição e apuração da assembleia, sem alterar o QGC do caso."
            :back="`/agcs/${meetingId}`" back-text="Detalhes da Assembleia">
            <input
                ref="qgcFileInput"
                type="file"
                accept=".csv,.xls,.xlsx"
                class="d-none"
                @change="onScenarioSpreadsheetSelected"
            />
            <ui-action-menu :items="[
                { header: 'Exportar' },
                { text: 'QGC do caso', icon: 'mdi-file-excel-outline', loading: exportLoading, handler: exportCaseQgc },
                { text: 'Cenário selecionado', icon: 'mdi-table-arrow-down', disabled: !selectedScenario, loading: scenarioExportLoading, handler: exportSelectedScenario },
                { text: 'Cédula Excel', icon: 'mdi-file-excel-outline', description: selectedScenario && selectedVotingId ? null : 'Selecione um cenário e uma votação', disabled: !selectedScenario || !selectedVotingId, loading: ballotExportLoading, handler: exportScenarioBallot },
                { divider: true },
                { text: 'Importar QGC', icon: 'mdi-file-upload-outline', loading: importLoading, handler: startScenarioImport },
                { divider: true },
                { text: 'Atualizar', icon: 'mdi-refresh', loading: loading, handler: loadAll },
            ]" />
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openNewScenario()">
                <v-icon left small>mdi-plus</v-icon>
                Novo cenário
            </v-btn>
        </ui-page-header>

        <div class="ui-card mb-4">
            <div class="ui-card__body pt-5">
                <v-row dense>
                    <v-col cols="12" md="5">
                        <v-text-field
                            dense
                            outlined
                            disabled
                            hide-details
                            label="Assembleia"
                            :value="meeting.name || meeting.title || meeting.description || `Assembleia #${meetingId}`"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select
                            dense
                            outlined
                            hide-details
                            clearable
                            label="Votação em contexto"
                            :items="votings"
                            item-text="name"
                            item-value="id"
                            v-model="selectedVotingId"
                            @change="onVotingChange"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select
                            dense
                            outlined
                            hide-details
                            clearable
                            label="Cenário em trabalho"
                            :items="scenarios"
                            item-text="name"
                            item-value="id"
                            v-model="selectedScenarioId"
                            @change="selectScenario"
                        ></v-select>
                    </v-col>
                </v-row>
                <v-alert dense text type="info" class="mb-0 mt-3">
                    Os cenários são da assembleia e valem para todas as votações. Os votos são registrados uma única vez e refletidos
                    em cada cenário marcado na votação (aba Comparação). Nenhum cenário altera o QGC do caso.
                </v-alert>
            </div>
        </div>

        <v-row dense class="mb-2">
            <v-col cols="6" md="3">
                <div class="ui-card metric-card">
                        <div class="metric-label">Cenários</div>
                        <div class="metric-value">{{ scenarios.length }}</div>
                </div>
            </v-col>
            <v-col cols="6" md="3">
                <div class="ui-card metric-card">
                        <div class="metric-label">Créditos considerados</div>
                        <div class="metric-value">{{ scenarioCreditStats.considered }}</div>
                        <div class="ui-small ui-muted">
                            de {{ scenarioCreditStats.total }} crédito(s) no cenário · {{ scenarioCreditStats.creditors }} credor(es)
                            <span v-if="scenarioCreditStats.disregarded"> · {{ scenarioCreditStats.disregarded }} desconsiderado(s)</span>
                        </div>
                </div>
            </v-col>
            <v-col cols="6" md="3">
                <div class="ui-card metric-card">
                        <div class="metric-label">Alterações</div>
                        <div class="metric-value">{{ selectedScenario ? selectedScenario.change_count || changes.length : 0 }}</div>
                </div>
            </v-col>
            <v-col cols="6" md="3">
                <div class="ui-card metric-card">
                        <div class="metric-label">Cenário oficial</div>
                        <div class="metric-value metric-value--text">{{ officialScenario ? officialScenario.name : "Não definido" }}</div>
                </div>
            </v-col>
        </v-row>

        <div class="ui-card workspace-card">
            <v-tabs v-model="activeTab" color="primary" show-arrows>
                <v-tab>Cenários <span class="ui-tab-count">{{ scenarios.length }}</span></v-tab>
                <v-tab>Créditos</v-tab>
                <v-tab>Alterações</v-tab>
                <v-tab>Resultado</v-tab>
                <v-tab>Comparação</v-tab>
                <v-tab>Documentos</v-tab>
                <v-tab>Auditoria</v-tab>
            </v-tabs>

            <v-divider></v-divider>

            <div v-if="selectedScenario && activeTab !== 0" class="scenario-back-bar px-4 py-2">
                <v-btn text color="primary" class="px-2" @click="backToScenarioList">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Voltar aos cenários
                </v-btn>
                <span class="ui-small ui-muted">
                    {{ selectedScenario.name }}
                </span>
            </div>

            <v-divider v-if="selectedScenario && activeTab !== 0"></v-divider>

            <v-tabs-items v-model="activeTab">
                <v-tab-item>
                    <v-card-text>
                        <ui-table-card>
                            <template #toolbar>
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    prepend-inner-icon="mdi-magnify"
                                    placeholder="Pesquisar cenário"
                                    v-model="scenarioSearch"
                                    class="ui-toolbar__search"
                                    @input="scenarioPage = 1"
                                ></v-text-field>
                                <span class="ui-toolbar__spacer"></span>
                                <span class="ui-small ui-muted">{{ statusSummary }}</span>
                            </template>

                            <v-data-table
                                :headers="scenarioHeaders"
                                :items="filteredScenarios"
                                :loading="loading"
                                disable-sort
                                hide-default-footer
                                :page.sync="scenarioPage"
                                :items-per-page="scenarioItemsPerPage"
                                class="ui-clickable-rows"
                                no-data-text="Nenhum cenário cadastrado"
                                loading-text="Carregando dados..."
                                @click:row="openScenarioRow"
                            >
                                <template v-slot:[`item.name`]="{ item }">
                                    <div class="py-2">
                                        <span class="ui-cell-main">{{ item.name }}</span>
                                        <ui-badge v-if="Number(item.status) === statuses.OFFICIAL" tone="primary" text="Oficial" class="ml-2" />
                                        <div class="ui-cell-sub">{{ item.description || "Sem descrição" }}</div>
                                    </div>
                                </template>
                                <template v-slot:[`item.status`]="{ item }">
                                    <ui-badge :text="statusName(item.status)" :tone="statusTone(item.status)" />
                                </template>
                                <template v-slot:[`item.composition`]="{ item }">
                                    <div>{{ item.participant_count || 0 }} créditos</div>
                                    <div class="ui-cell-sub">{{ item.eligible_count || 0 }} com direito de voto</div>
                                </template>
                                <template v-slot:[`item.changes`]="{ item }">
                                    {{ item.change_count || 0 }}
                                </template>
                                <template v-slot:[`item.actions`]="{ item }">
                                    <ui-row-actions :actions="[
                                        { text: 'Abrir cenário', icon: 'mdi-eye-outline', handler: () => selectScenario(item.id) },
                                        { text: 'Duplicar como rascunho', icon: 'mdi-content-copy', handler: () => duplicateScenario(item) },
                                        { text: advanceTooltip(item), icon: advanceIcon(item), disabled: !canAdvance(item), handler: () => advanceScenario(item) },
                                        { text: canDelete(item) ? 'Excluir cenário' : 'Somente rascunhos ou arquivados sem vínculo podem ser excluídos', icon: 'mdi-delete-outline', danger: true, disabled: !canDelete(item), handler: () => deleteScenario(item) },
                                    ]" />
                                </template>
                            </v-data-table>

                            <template #footer>
                                <ui-pagination :total="filteredScenarios.length" :page.sync="scenarioPage" :items-per-page.sync="scenarioItemsPerPage" />
                            </template>
                        </ui-table-card>
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <v-alert v-if="!selectedScenario" type="info" text>
                            Selecione um cenário para consultar os créditos.
                        </v-alert>
                        <template v-else>
                            <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap: 12px;">
                                <div>
                                    <h3 class="ui-section-title">{{ selectedScenario.name }}</h3>
                                    <p class="ui-section-description">
                                        {{ selectedScenario.is_editable ? "Cenário editável" : "Somente leitura" }}
                                    </p>
                                </div>
                                <div class="d-flex" style="gap: 8px;">
                                    <v-btn
                                        color="primary"
                                        outlined
                                        elevation="0"
                                        :disabled="!selectedScenario.is_editable"
                                        @click="addManualCredit"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        Crédito manual
                                    </v-btn>
                                    <v-btn v-if="$allowed('save')"
                                        color="primary"
                                        depressed
                                        :disabled="!selectedScenario.is_editable"
                                        :loading="saving"
                                        @click="saveCredits"
                                    >
                                        Salvar alterações
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Efeito de cada marcacao sobre a base de cabecas e de creditos -->
                            <v-alert dense text type="info" class="mb-4 scenario-help">
                                <div class="font-weight-medium mb-1">Como cada marcação afeta a apuração</div>
                                <div class="scenario-help-grid">
                                    <div>
                                        <strong>Sem direito de voto</strong> (chave "Direito de voto" desligada):
                                        o crédito continua na base de presença e no quórum de instalação, mas sai da base de
                                        <strong>cabeças</strong> e de <strong>créditos</strong> da votação. O credor não consegue votar por ele.
                                    </div>
                                    <div>
                                        <strong>Desconsiderado</strong>:
                                        o crédito sai da apuração deste cenário, da base de
                                        <strong>cabeças</strong>, da base de <strong>créditos</strong> e da votação. O QGC oficial do caso não muda.
                                    </div>
                                </div>
                                <div class="text-caption mt-1">
                                    Cabeças contam credores distintos com voto SIM ou NÃO; abstenções e sem voto não entram no cálculo da maioria.
                                    Créditos somam o valor considerado desses mesmos votos. A presença e o quórum usam o QGC ou o cenário oficializado; cenários de simulação não alteram as demais sessões. O quadro de instalação já registrado permanece fixo.
                                </div>
                            </v-alert>
                            <ui-table-card>
                            <template #toolbar>
                                <v-text-field
                                    dense
                                    outlined
                                    clearable
                                    hide-details
                                    prepend-inner-icon="mdi-magnify"
                                    placeholder="Pesquisar por credor, CPF/CNPJ ou referência"
                                    v-model="creditSearch"
                                    class="ui-toolbar__search"
                                    @input="creditPage = 1"
                                ></v-text-field>
                            </template>

                            <v-data-table
                                :headers="creditHeaders"
                                :items="filteredCredits"
                                :loading="creditsLoading"
                                disable-sort
                                hide-default-footer
                                :page.sync="creditPage"
                                :items-per-page="creditItemsPerPage"
                                class="scenario-credit-table"
                                no-data-text="Nenhum crédito encontrado"
                                loading-text="Carregando dados..."
                            >
                                <template v-slot:[`item.creditor`]="{ item }">
                                    <div v-if="item.local_id && selectedScenario.is_editable" class="manual-credit-fields py-1">
                                        <v-autocomplete
                                            dense
                                            outlined
                                            hide-details
                                            clearable
                                            cache-items
                                            :loading="qgcSearchLoading"
                                            :items="qgcCreditorOptions"
                                            :item-text="qgcCreditorLabel"
                                            item-value="id"
                                            label="Credor no QGC"
                                            placeholder="Busque por nome ou CPF/CNPJ"
                                            v-model="item.qgc_creditor_id"
                                            :search-input.sync="item.creditor_search"
                                            @update:search-input="searchQgcCreditors(item, $event)"
                                            @change="applyQgcCreditor(item, $event)"
                                        ></v-autocomplete>
                                        <v-text-field
                                            dense
                                            outlined
                                            hide-details
                                            label="CPF/CNPJ"
                                            placeholder="Informe o CPF ou CNPJ"
                                            v-mask="(item.registration || '').replace(/\D/g, '').length <= 11 ? $masks.cpf : $masks.cnpj"
                                            v-model="item.registration"
                                        ></v-text-field>
                                    </div>
                                    <div v-else>
                                        <div class="ui-cell-main">{{ item.name }}</div>
                                        <div class="ui-cell-sub">{{ item.registration }} · {{ item.credit_reference || "sem referência" }}</div>
                                        <div v-if="item.client_company_name" class="text-caption primary--text">{{ item.client_company_name }}</div>
                                    </div>
                                </template>
                                <template v-slot:[`item.scenario_class`]="{ item }">
                                    <div class="credit-editor-stack">
                                        <v-select
                                            dense
                                            outlined
                                            hide-details
                                            :disabled="!selectedScenario.is_editable || item.disregarded"
                                            :items="creditorTypes"
                                            item-text="name"
                                            item-value="id"
                                            v-model="item.scenario_class"
                                        ></v-select>
                                        <div v-if="hasClassChange(item)" class="ui-cell-sub original-value">
                                            Original: {{ className(item.base_creditor_class) }}
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:[`item.amount`]="{ item }">
                                    <div class="credit-editor-stack">
                                        <v-currency-field
                                            dense
                                            outlined
                                            hide-details
                                            :disabled="!selectedScenario.is_editable || item.disregarded"
                                            :allow-negative="false"
                                            v-model="item.amount"
                                        ></v-currency-field>
                                        <div v-if="hasAmountChange(item)" class="ui-cell-sub original-value">
                                            Original: {{ formatAmount(item.base_amount) }}
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:[`item.vote_eligible`]="{ item }">
                                    <div class="credit-control-center">
                                        <v-switch
                                            dense
                                            hide-details
                                            inset
                                            :disabled="!selectedScenario.is_editable || item.disregarded"
                                            v-model="item.vote_eligible"
                                        ></v-switch>
                                    </div>
                                </template>
                                <template v-slot:[`item.disregarded`]="{ item }">
                                    <div class="credit-control-center">
                                        <v-checkbox
                                            dense
                                            hide-details
                                            :disabled="!selectedScenario.is_editable"
                                            v-model="item.disregarded"
                                        ></v-checkbox>
                                    </div>
                                </template>
                                <template v-slot:[`item.change_reason`]="{ item }">
                                    <v-text-field
                                        dense
                                        outlined
                                        hide-details
                                        :disabled="!selectedScenario.is_editable"
                                        placeholder="Fundamento da alteração"
                                        v-model="item.change_reason"
                                    ></v-text-field>
                                </template>
                                <template v-slot:[`item.actions`]="{ item }">
                                    <ui-row-actions :actions="[
                                        { text: 'Ver detalhes e documentos', icon: 'mdi-eye-outline', handler: () => openCreditDetails(item) },
                                        { text: 'Excluir crédito incluído', icon: 'mdi-delete-outline', danger: true, hidden: !(item.local_id && selectedScenario.is_editable), handler: () => removeCredit(item) , disabled: !$allowed('delete') },
                                    ]" />
                                </template>
                            </v-data-table>

                            <template #footer>
                                <ui-pagination :total="filteredCredits.length" :page.sync="creditPage" :items-per-page.sync="creditItemsPerPage"
                                    :items-per-page-options="[15, 30, 50, 100]" />
                            </template>
                            </ui-table-card>
                        </template>
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <v-alert v-if="!selectedScenario" type="info" text>Selecione um cenário.</v-alert>
                        <ui-table-card v-else>
                            <v-data-table
                                :headers="changeHeaders"
                                :items="changes"
                                disable-sort
                                hide-default-footer
                                :page.sync="changePage"
                                :items-per-page="changeItemsPerPage"
                                class="ui-clickable-rows"
                                no-data-text="Este cenário não possui alterações em relação à sua base"
                                @click:row="openChangeDetails"
                            >
                                <template v-slot:[`item.creditor`]="{ item }">
                                    <span class="ui-cell-main">{{ item.creditor }}</span>
                                </template>
                                <template v-slot:[`item.actions`]>
                                    <v-icon small class="ui-cell-chevron" title="Ver fundamento e documentos">mdi-chevron-right</v-icon>
                                </template>
                            </v-data-table>

                            <template #footer>
                                <ui-pagination :total="changes.length" :page.sync="changePage" :items-per-page.sync="changeItemsPerPage"
                                    :items-per-page-options="[15, 30, 50, 100]" />
                            </template>
                        </ui-table-card>
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <v-alert v-if="!selectedScenario" type="info" text>Selecione um cenário.</v-alert>
                        <template v-else>
                            <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap: 12px;">
                                <div>
                                    <h3 class="ui-section-title">Resultado do cenário</h3>
                                    <p class="ui-section-description">
                                        {{ scenarioResult ? `Apurado em ${formatDate(scenarioResult.generated_at)}` : "Ainda não apurado" }}
                                    </p>
                                </div>
                                <div class="d-flex flex-wrap" style="gap: 8px;">
                                    <v-btn
                                        v-if="scenarioResult"
                                        color="primary"
                                        outlined
                                        elevation="0"
                                        :loading="resultExportLoading"
                                        @click="exportScenarioResult"
                                    >
                                        <v-icon left small>mdi-file-pdf-box</v-icon>
                                        Exportar resultado
                                    </v-btn>
                                    <v-btn
                                        color="primary"
                                        depressed
                                        :loading="resultLoading"
                                        :disabled="!selectedVotingId
                                            || Number(selectedScenario.status) === statuses.ARCHIVED
                                            || (!selectedScenario.is_editable && !!scenarioResult)"
                                        @click="calculateResult"
                                    >
                                        <v-icon left small>mdi-calculator-variant-outline</v-icon>
                                        {{ scenarioResult ? "Reapurar resultado" : "Apurar resultado" }}
                                    </v-btn>
                                </div>
                            </div>

                            <v-alert v-if="!selectedVotingId" type="warning" text>
                                Selecione uma votação no campo “Votação em contexto” para aplicar os votos a este cenário.
                            </v-alert>
                            <v-alert v-else-if="scenarioBlockingIssues.length" type="error" text>
                                <strong>
                                    Corrija {{ scenarioBlockingIssues.length }} crédito(s) antes de apurar o resultado:
                                </strong>
                                <ul class="mt-2 mb-0">
                                    <li v-for="item in scenarioBlockingIssues" :key="item.key">
                                        <strong>{{ item.credit }}</strong>: {{ item.message }}.
                                    </li>
                                </ul>
                            </v-alert>
                            <v-alert v-else-if="!scenarioResult" type="info" text>
                                Clique em “Apurar resultado”. Os votos já registrados serão aplicados à composição, classe e valores deste cenário.
                            </v-alert>

                            <template v-else>
                                <v-alert v-if="scenarioResult.ordinary.summary.head_count_basis === 'voter_class'" type="info" text dense>
                                    Os votos são contados por credor em cada classe. Um credor com créditos em duas classes pode ter votos diferentes; em “Credores considerados”, ele é contado uma única vez.
                                </v-alert>
                                <v-row dense class="mb-3">
                                    <v-col cols="6" md="3">
                                        <div class="ui-card metric-card result-metric">
                                                <div class="metric-label">Credores considerados</div>
                                                <div class="metric-value">{{ scenarioResult.eligible_voter_count }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <div class="ui-card metric-card result-metric">
                                                <div class="metric-label">Votos SIM</div>
                                                <div class="metric-value success--text">{{ scenarioResult.ordinary.summary.yes_heads }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <div class="ui-card metric-card result-metric">
                                                <div class="metric-label">Votos NÃO</div>
                                                <div class="metric-value error--text">{{ scenarioResult.ordinary.summary.no_heads }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <div class="ui-card metric-card result-metric">
                                                <div class="metric-label">Sem voto</div>
                                                <div class="metric-value warning--text">{{ scenarioResult.ordinary.summary.pending_heads }}</div>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-alert
                                    text
                                    :type="scenarioResult.ordinary.summary && scenarioResult.ordinary.summary.tie && scenarioResult.ordinary.voting_type === 1 ? 'warning' : (scenarioResult.ordinary.approved ? 'success' : 'error')"
                                    class="mb-4"
                                >
                                    <strong>
                                        {{ scenarioResult.ordinary.approved
                                            ? "Aprovado" : "Rejeitado" }}
                                    </strong>
                                    <span v-if="scenarioResult.ordinary.summary && scenarioResult.ordinary.summary.tie && scenarioResult.ordinary.voting_type === 1"> · Empate por cabeça</span>
                                </v-alert>

                                <ui-table-card class="mb-4">
                                    <v-data-table
                                        :headers="resultHeaders"
                                        :items="scenarioResult.ordinary.classes"
                                        disable-sort
                                        hide-default-footer
                                        no-data-text="Esta votação não utiliza apuração por classe"
                                    >
                                        <template v-slot:[`item.class_name`]="{ item }">
                                            <div class="ui-cell-main">{{ item.class_name }}</div>
                                            <div class="ui-cell-sub">{{ item.rule }}</div>
                                        </template>
                                        <template v-slot:[`item.heads`]="{ item }">
                                            {{ item.yes_heads }} de {{ item.effective_heads }}
                                            <div class="ui-cell-sub">{{ formatPercent(item.head_percentage) }}</div>
                                        </template>
                                        <template v-slot:[`item.amount`]="{ item }">
                                            <template v-if="item.requires_amount">
                                                {{ formatAmount(item.yes_amount) }} de {{ formatAmount(item.effective_amount) }}
                                                <div class="ui-cell-sub">{{ formatPercent(item.amount_percentage) }}</div>
                                            </template>
                                            <span v-else class="ui-muted">Não aplicável</span>
                                        </template>
                                        <template v-slot:[`item.status`]="{ item }">
                                            <ui-badge
                                                :color="!item.applicable ? 'grey' : item.tie ? 'warning' : (item.approved ? 'success' : 'error')"
                                                :text='!item.applicable ? "Sem créditos" : item.approved ? "Aprovado" : "Rejeitado"'
                                            />
                                            <span v-if="item.applicable && item.tie" class="warning--text ml-2">Empate</span>
                                        </template>
                                    </v-data-table>
                                </ui-table-card>

                                <ui-section v-if="scenarioResult.cram_down && scenarioResult.cram_down.applies" title="Análise separada de cram down">
                                    <v-alert
                                        dense
                                        text
                                        :type="scenarioResult.cram_down.eligible_for_judicial_decision ? 'warning' : 'info'"
                                    >
                                        {{ scenarioResult.cram_down.eligible_for_judicial_decision
                                            ? "Os critérios quantitativos foram atendidos. A aprovação depende de decisão judicial e da verificação de tratamento não diferenciado."
                                            : "Os critérios quantitativos para cram down não foram integralmente atendidos." }}
                                    </v-alert>
                                    <div class="cram-grid">
                                        <div>
                                            <v-icon small :color="criterionColor(scenarioResult.cram_down.criteria.more_than_half_total_present_value)">
                                                {{ criterionIcon(scenarioResult.cram_down.criteria.more_than_half_total_present_value) }}
                                            </v-icon>
                                            Mais da metade do valor presente
                                        </div>
                                        <div>
                                            <v-icon small :color="criterionColor(scenarioResult.cram_down.criteria.minimum_classes_approved)">
                                                {{ criterionIcon(scenarioResult.cram_down.criteria.minimum_classes_approved) }}
                                            </v-icon>
                                            Classes mínimas aprovadas
                                        </div>
                                        <div>
                                            <v-icon small :color="criterionColor(scenarioResult.cram_down.criteria.one_third_in_each_rejecting_class)">
                                                {{ criterionIcon(scenarioResult.cram_down.criteria.one_third_in_each_rejecting_class) }}
                                            </v-icon>
                                            Mais de 1/3 nas classes rejeitantes
                                        </div>
                                        <div>
                                            <v-icon small color="warning">mdi-alert-circle-outline</v-icon>
                                            Tratamento não diferenciado — validação jurídica
                                        </div>
                                    </div>
                                </ui-section>
                            </template>
                        </template>
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <v-alert v-if="!selectedVotingId" type="info" text>
                            Selecione a votação no campo “Votação em contexto” para comparar os cenários com os votos dela.
                        </v-alert>
                        <template v-else>
                            <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                                <div class="ui-small ui-muted" style="flex: 1; min-width: 260px;">
                                    Marque os cenários desta votação. Os mesmos votos são apurados em cada um e a seleção fica salva na votação.
                                    O cenário base define quem vota e não pode ser desmarcado aqui.
                                </div>
                                <v-btn v-if="$allowed('edit')" color="primary" depressed :loading="comparison.loading"
                                    :disabled="!comparison.selected.length" @click="calculateComparison">
                                    <v-icon left small>mdi-calculator-variant-outline</v-icon>
                                    Apurar marcados ({{ comparison.selected.length }})
                                </v-btn>
                            </div>
                            <v-simple-table dense class="comparison-table">
                                <thead>
                                    <tr>
                                        <th style="width: 44px;"></th>
                                        <th>Cenário</th>
                                        <th>Status</th>
                                        <th class="text-right">Credores</th>
                                        <th class="text-right">SIM / NÃO (cabeças)</th>
                                        <th class="text-right">SIM / NÃO (crédito)</th>
                                        <th>Resultado</th>
                                        <th>Apurado em</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in scenarios" :key="item.id">
                                        <td>
                                            <v-simple-checkbox :value="comparison.selected.includes(item.id)" :disabled="isBaseScenario(item)"
                                                @input="toggleComparison(item.id, $event)" />
                                        </td>
                                        <td>
                                            <a href="#" @click.prevent="openScenarioResult(item)">{{ item.name }}</a>
                                            <ui-badge v-if="isBaseScenario(item)" text="Base da votação" tone="info" class="ml-1" />
                                        </td>
                                        <td><ui-badge :text="statusName(item.status)" :tone="statusTone(item.status)" /></td>
                                        <template v-if="comparisonResult(item)">
                                            <td class="text-right">{{ comparisonResult(item).eligible_voter_count }}</td>
                                            <td class="text-right">
                                                {{ comparisonSummary(item).yes_heads || 0 }} / {{ comparisonSummary(item).no_heads || 0 }}
                                            </td>
                                            <td class="text-right">
                                                <span v-if="Number(comparisonSummary(item).effective_amount || 0) > 0">
                                                    {{ formatAmount(comparisonSummary(item).yes_amount) }} / {{ formatAmount(comparisonSummary(item).no_amount) }}
                                                </span>
                                                <span v-else class="ui-muted">—</span>
                                            </td>
                                            <td>
                                                <ui-badge :text="comparisonVerdict(item).text" :tone="comparisonVerdict(item).tone" />
                                            </td>
                                        </template>
                                        <td v-else colspan="4" :class="comparison.errors[item.id] ? 'error--text' : 'ui-muted'">
                                            {{ comparison.errors[item.id] || 'Não apurado nesta votação' }}
                                        </td>
                                        <td class="ui-small">{{ item.voting_result_at ? formatDate(item.voting_result_at) : '—' }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </template>
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <scenario-documents :scenario="selectedScenario" :credits="credits" />
                    </v-card-text>
                </v-tab-item>

                <v-tab-item>
                    <v-card-text>
                        <v-alert v-if="!selectedScenario" type="info" text>Selecione um cenário.</v-alert>
                        <v-timeline v-else dense align-top>
                            <v-timeline-item
                                v-for="item in auditLogs"
                                :key="item.id"
                                small
                                color="primary"
                            >
                                <div class="font-weight-medium">{{ auditActionName(item.action) }}</div>
                                <div class="ui-small ui-muted">
                                    {{ formatDate(item.created_at) }} · {{ item.admin ? item.admin.name : "Sistema" }}
                                </div>
                                <details v-if="item.details" class="mt-2">
                                    <summary class="primary--text">Ver alterações e dados do evento</summary>
                                    <pre class="pa-3 grey lighten-4 rounded" style="white-space: pre-wrap; overflow-wrap: anywhere; max-height: 360px; overflow: auto">{{ formatAuditDetails(item.details) }}</pre>
                                </details>
                            </v-timeline-item>
                        </v-timeline>
                    </v-card-text>
                </v-tab-item>
            </v-tabs-items>
        </div>

        <v-dialog v-model="scenarioDialog" max-width="720px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Novo cenário</h3>
                    <v-btn icon small @click="closeScenarioDialog" :disabled="saving">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="ui-card__body">
                    <v-alert dense text type="info">
                        O cenário será criado como rascunho. Os campos começam vazios, inclusive quando a origem for uma planilha.
                    </v-alert>

                    <v-alert
                        v-if="importPreview && importPreview.substantial_consolidation"
                        dense
                        text
                        type="warning"
                    >
                        <template v-if="Number(importPreview.consolidation_type) === 2">
                            Este caso está em consolidação substancial (QGC único do grupo).
                        </template>
                        <template v-else-if="Number(importPreview.consolidation_type) === 1">
                            Este caso está em consolidação processual (um QGC por empresa).
                        </template>
                        <template v-else>
                            Este caso tem empresas do grupo sem regime de consolidação cadastrado.
                        </template>
                        A empresa devedora integra a identificação de cada crédito e será preservada na importação.
                    </v-alert>
                    <v-text-field dense outlined label="Nome do cenário" v-model="scenarioForm.name"></v-text-field>
                    <v-textarea dense outlined rows="2" label="Descrição resumida" v-model="scenarioForm.description"></v-textarea>
                    <v-textarea dense outlined rows="2" label="Fundamento jurídico" v-model="scenarioForm.legal_basis"></v-textarea>
                    <v-textarea dense outlined rows="2" label="Observações internas" v-model="scenarioForm.internal_notes"></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="closeScenarioDialog" :disabled="saving">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed @click="createScenario" :loading="saving">
                        Criar rascunho
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Passo final da importacao: o seletor de arquivo so abre por clique direto -->
        <v-dialog v-model="importPickerDialog" max-width="520px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Importar QGC</h3>
                    <v-btn icon small @click="importPickerDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <div class="ui-card__body">
                    <p class="mb-2">
                        Cenário <strong>{{ selectedScenario ? selectedScenario.name : '' }}</strong> pronto para receber a planilha.
                    </p>
                    <p class="mb-0 ui-small ui-muted">
                        Formatos aceitos: CSV, XLS e XLSX. Depois da leitura você confere as diferenças antes de aplicar.
                    </p>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="importPickerDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed @click="pickSpreadsheetNow">
                        <v-icon left small>mdi-file-upload-outline</v-icon> Selecionar planilha
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="importTargetDialog" max-width="720px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Escolher destino da importação</h3>
                    <v-btn icon small @click="importTargetDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="ui-card__body">
                    <v-alert type="warning" text dense>
                        O cenário selecionado está {{ selectedScenario ? statusName(selectedScenario.status).toLowerCase() : "indisponível" }}
                        e não pode ser alterado. O QGC oficial do caso nunca será modificado.
                    </v-alert>
                    <v-btn v-if="$allowed('create')" block outlined color="primary" class="mb-3" @click="duplicateSelectedForImport">
                        <v-icon left>mdi-content-copy</v-icon>
                        Duplicar cenário atual e importar
                    </v-btn>
                    <v-btn v-if="$allowed('create')" block outlined color="primary" class="mb-3" @click="openNewScenarioForImport">
                        <v-icon left>mdi-file-plus-outline</v-icon>
                        Criar novo cenário a partir da planilha
                    </v-btn>
                    <v-select
                        dense
                        outlined
                        label="Ou selecionar outro cenário em rascunho"
                        :items="editableScenarios"
                        item-text="name"
                        item-value="id"
                        v-model="importDraftTargetId"
                    ></v-select>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="importTargetDialog = false">Cancelar</v-btn>
                    <v-btn
                        color="primary"
                        depressed
                        :disabled="!importDraftTargetId"
                        @click="selectDraftForImport"
                    >
                        Continuar
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="importPreviewDialog" max-width="1280px" persistent>
            <v-card>
                <v-card-title class="modal-title d-flex justify-space-between align-center">
                    <span>Prévia da importação do QGC</span>
                    <v-btn icon @click="closeImportPreview" :disabled="importApplying">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-5">
                    <v-alert dense text type="info">
                        A planilha será aplicada somente ao cenário <strong>{{ selectedScenario ? selectedScenario.name : "" }}</strong>.
                        Nenhum dado do QGC oficial será alterado.
                    </v-alert>

                    <v-row dense v-if="importPreview">
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Total</div><div class="metric-value">{{ importPreview.summary.total }}</div></v-card-text></v-card>
                        </v-col>
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Válidas</div><div class="metric-value success--text">{{ importPreview.summary.valid }}</div></v-card-text></v-card>
                        </v-col>
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Sem alteração</div><div class="metric-value">{{ importPreview.summary.unchanged }}</div></v-card-text></v-card>
                        </v-col>
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Alteradas</div><div class="metric-value warning--text">{{ importPreview.summary.changed }}</div></v-card-text></v-card>
                        </v-col>
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Novas</div><div class="metric-value primary--text">{{ importPreview.summary.new }}</div></v-card-text></v-card>
                        </v-col>
                        <v-col cols="6" md="2">
                            <v-card outlined><v-card-text><div class="metric-label">Pendentes</div><div class="metric-value error--text">{{ importPreview.summary.pending }}</div></v-card-text></v-card>
                        </v-col>
                    </v-row>

                    <v-alert
                        v-if="importPreview && importPreview.summary.ambiguous"
                        type="error"
                        text
                        dense
                        class="mt-3"
                    >
                        Existem {{ importPreview.summary.ambiguous }} correspondências ambíguas.
                        Selecione o crédito correto na coluna “Validação” antes de aplicar.
                    </v-alert>

                    <v-radio-group v-model="importMode" row class="mt-3">
                        <v-radio value="differences" label="Aplicar somente as diferenças"></v-radio>
                        <v-radio value="replace" label="Substituir integralmente a composição do cenário"></v-radio>
                    </v-radio-group>

                    <v-checkbox
                        v-if="importPreview && importPreview.summary.invalid"
                        v-model="discardInvalidImportRows"
                        :label="`Descartar as ${importPreview.summary.invalid} linhas inválidas ao aplicar`"
                    ></v-checkbox>

                    <v-data-table
                        v-if="importPreview"
                        :headers="importHeaders"
                        :items="importPreview.rows"
                        :items-per-page="15"
                        class="app-table"
                    >
                        <template v-slot:[`item.creditor`]="{ item }">
                            <strong>{{ item.proposed ? item.proposed.name : item.name || "—" }}</strong>
                            <div class="text-caption grey--text">
                                {{ item.proposed ? item.proposed.registration : item.registration }}
                                · {{ item.proposed ? item.proposed.credit_reference || "sem referência" : item.reference || "sem referência" }}
                            </div>
                            <div v-if="item.proposed && item.proposed.client_company_name" class="text-caption primary--text">
                                {{ item.proposed.client_company_name }}
                            </div>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <ui-badge :text="importStatusName(item.status)" :tone="importStatusTone(item.status)" />
                        </template>
                        <template v-slot:[`item.current`]="{ item }">
                            <span v-if="item.current">
                                {{ className(item.current.creditor_class) }} · {{ formatAmount(item.current.amount) }}
                            </span>
                            <span v-else>—</span>
                        </template>
                        <template v-slot:[`item.proposed`]="{ item }">
                            <span v-if="item.proposed">
                                {{ className(item.proposed.creditor_class) }} · {{ formatAmount(item.proposed.amount) }}
                            </span>
                            <span v-else>—</span>
                        </template>
                        <template v-slot:[`item.errors`]="{ item }">
                            <v-select
                                v-if="item.status === 'ambiguous'"
                                dense
                                outlined
                                hide-details
                                placeholder="Selecione o crédito"
                                :items="item.candidates"
                                item-text="label"
                                item-value="participant_id"
                                @change="resolveImportAmbiguity(item, $event)"
                            ></v-select>
                            <span v-else>{{ item.errors && item.errors.length ? item.errors.join(" ") : "—" }}</span>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text @click="closeImportPreview" :disabled="importApplying">Cancelar</v-btn>
                    <v-btn
                        color="primary"
                        elevation="0"
                        :loading="importApplying"
                        :disabled="!canApplyImport"
                        @click="applyScenarioImport"
                    >
                        Aplicar planilha ao cenário
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="creditDetailDialog" max-width="760px" scrollable>
            <v-card v-if="detailCredit" class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Detalhes do crédito</h3></div>
                <v-card-text class="ui-card__body">
                    <div class="ui-cell-main">{{ detailCredit.name }}</div>
                    <div class="ui-cell-sub mb-4">{{ detailCredit.registration }} · {{ detailCredit.credit_reference || "sem referência" }}</div>
                    <v-row dense>
                        <v-col cols="6"><strong>Classe</strong><div>{{ className(detailCredit.scenario_class) }}</div></v-col>
                        <v-col cols="6"><strong>Valor</strong><div>{{ formatAmount(detailCredit.amount) }}</div></v-col>
                        <v-col cols="12"><strong>Fundamento</strong><div>{{ detailCredit.change_reason || "Não informado" }}</div></v-col>
                    </v-row>
                    <v-divider class="my-4" />
                    <div class="font-weight-medium mb-2">Documentos vinculados</div>
                    <v-list dense v-if="detailDocuments.length">
                        <v-list-item v-for="documentItem in detailDocuments" :key="documentItem.id">
                            <v-list-item-content><v-list-item-title>{{ documentItem.name }}</v-list-item-title><v-list-item-subtitle>{{ documentItem.description || documentItem.category }}</v-list-item-subtitle></v-list-item-content>
                            <v-list-item-action><v-btn icon @click="downloadScenarioDocument(documentItem)"><v-icon>mdi-download</v-icon></v-btn></v-list-item-action>
                        </v-list-item>
                    </v-list>
                    <v-alert v-else dense text type="info">Nenhum documento foi vinculado a este crédito.</v-alert>
                </v-card-text>
                <div class="ui-card__footer"><v-btn text @click="creditDetailDialog = false">Fechar</v-btn></div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="changeDetailDialog" max-width="680px">
            <v-card v-if="detailChange" class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Detalhes da alteração</h3></div>
                <div class="ui-card__body">
                    <div class="ui-cell-main">{{ detailChange.creditor }}</div><div class="ui-cell-sub mb-3">{{ detailChange.reference }}</div>
                    <v-row dense><v-col cols="4"><strong>Alteração</strong><div>{{ detailChange.type }}</div></v-col><v-col cols="4"><strong>Antes</strong><div>{{ detailChange.before }}</div></v-col><v-col cols="4"><strong>No cenário</strong><div>{{ detailChange.after }}</div></v-col></v-row>
                    <div class="mt-4"><strong>Fundamento</strong><div>{{ detailChange.reason }}</div></div>
                    <v-divider class="my-4" />
                    <div class="font-weight-medium mb-2">Documentos vinculados</div>
                    <v-list dense v-if="detailDocuments.length"><v-list-item v-for="documentItem in detailDocuments" :key="documentItem.id"><v-list-item-content><v-list-item-title>{{ documentItem.name }}</v-list-item-title></v-list-item-content><v-list-item-action><v-btn icon @click="downloadScenarioDocument(documentItem)"><v-icon>mdi-download</v-icon></v-btn></v-list-item-action></v-list-item></v-list>
                    <v-alert v-else dense text type="info">Nenhum documento foi vinculado a esta alteração.</v-alert>
                </div>
                <div class="ui-card__footer"><v-btn text @click="changeDetailDialog = false">Fechar</v-btn></div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import creditorTypes from "@/common/data/creditor-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import ScenarioDocuments from "@/views/general-meeting/components/ScenarioDocuments.vue";

const STATUSES = Object.freeze({
    DRAFT: 1,
    READY: 2,
    FINALIZED: 3,
    OFFICIAL: 4,
    ARCHIVED: 5,
});

export default {
    name: "VotingScenarios",
    components: { ScenarioDocuments },

    data: () => ({
        statuses: STATUSES,
        loading: false,
        saving: false,
        creditsLoading: false,
        resultLoading: false,
        importLoading: false,
        exportLoading: false,
        scenarioExportLoading: false,
        ballotExportLoading: false,
        resultExportLoading: false,
        qgcSearchLoading: false,
        qgcCreditorOptions: [],
        qgcSearchTimer: null,
        importApplying: false,
        activeTab: 0,
        meeting: {},
        votings: [],
        scenarios: [],
        credits: [],
        auditLogs: [],
        selectedVotingId: null,
        selectedScenarioId: null,
        // Cenarios marcados na votacao em contexto (checkbox) e erros da ultima apuracao.
        comparison: {
            selected: [],
            loading: false,
            errors: {},
        },
        scenarioSearch: "",
        creditSearch: "",
        scenarioPage: 1,
        scenarioItemsPerPage: 10,
        creditPage: 1,
        creditItemsPerPage: 15,
        changePage: 1,
        changeItemsPerPage: 15,
        scenarioDialog: false,
        importTargetDialog: false,
        importPickerDialog: false,
        importPreviewDialog: false,
        importPreview: null,
        creditDetailDialog: false,
        changeDetailDialog: false,
        detailCredit: null,
        detailChange: null,
        detailDocuments: [],
        importMode: "differences",
        importDraftTargetId: null,
        discardInvalidImportRows: false,
        pendingImportAfterCreate: false,
        creditorTypes,
        scenarioForm: {
            name: "",
            description: "",
            legal_basis: "",
            internal_notes: "",
            source_type: "qgc",
        },
        scenarioHeaders: [
            { text: "Cenário", value: "name" },
            { text: "Status", value: "status", width: 150 },
            { text: "Composição", value: "composition", width: 180 },
            { text: "Alterações", value: "changes", width: 110 },
            { text: "Atualizado em", value: "updated_at", width: 170 },
            { text: "", value: "actions", sortable: false, align: "end", width: 56 },
        ],
        creditHeaders: [
            { text: "Credor · CPF/CNPJ · crédito", value: "creditor", width: 340 },
            { text: "Classe considerada", value: "scenario_class", width: 210 },
            { text: "Valor considerado", value: "amount", width: 190 },
            { text: "Direito de voto", value: "vote_eligible", width: 130, align: "center" },
            { text: "Desconsiderado", value: "disregarded", width: 130, align: "center" },
            { text: "Fundamento", value: "change_reason", width: 280 },
            { text: "", value: "actions", sortable: false, align: "end", width: 56 },
        ],
        changeHeaders: [
            { text: "Credor", value: "creditor" },
            { text: "Crédito", value: "reference" },
            { text: "Alteração", value: "type" },
            { text: "Antes", value: "before" },
            { text: "No cenário", value: "after" },
            { text: "Fundamento", value: "reason" },
            { text: "", value: "actions", sortable: false, align: "end", width: 56 },
        ],
        resultHeaders: [
            { text: "Classe", value: "class_name" },
            { text: "Cabeça", value: "heads" },
            { text: "Crédito", value: "amount" },
            { text: "Resultado", value: "status", sortable: false },
        ],
        importHeaders: [
            { text: "Linha", value: "row", width: 75 },
            { text: "Credor · crédito", value: "creditor" },
            { text: "Situação", value: "status", width: 130 },
            { text: "No cenário", value: "current" },
            { text: "Na planilha", value: "proposed" },
            { text: "Validação", value: "errors" },
        ],
    }),

    computed: {
        // Cabecalho do cenario: creditos (linhas), creditos considerados na apuracao e
        // credores distintos (cabecas). Le a tabela em edicao para refletir na hora.
        scenarioCreditStats() {
            const rows = Array.isArray(this.credits) && this.credits.length
                ? this.credits
                : null;
            if (!rows) {
                const scenario = this.selectedScenario || {};
                return {
                    total: Number(scenario.participant_count || 0),
                    considered: Number(scenario.eligible_count || 0),
                    disregarded: 0,
                    creditors: Number(scenario.creditor_count || 0),
                };
            }
            const considered = rows.filter((item) => !item.disregarded && !item.blocked && item.vote_eligible !== false);
            const keys = new Set(rows.map((item) => {
                const registration = String(item.registration || '').replace(/\D/g, '');
                return registration || (item.user_id ? `user:${item.user_id}` : `uc:${item.user_creditor_id || item.id}`);
            }));
            return {
                total: rows.length,
                considered: considered.length,
                disregarded: rows.filter((item) => !!item.disregarded).length,
                creditors: keys.size,
            };
        },

        meetingId() {
            return Number(this.$route.params.id);
        },

        breadcrumbs() {
            return [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Assembleias", disabled: false, to: "/agcs", exact: true },
                { text: "Detalhes da Assembleia", disabled: false, to: `/agcs/${this.meetingId}`, exact: true },
                { text: "Cenários", disabled: true },
            ];
        },

        selectedScenario() {
            return this.scenarios.find((item) => Number(item.id) === Number(this.selectedScenarioId)) || null;
        },

        officialScenario() {
            return this.scenarios.find((item) => Number(item.status) === STATUSES.OFFICIAL) || null;
        },

        editableScenarios() {
            return this.scenarios.filter((item) => item.is_editable);
        },

        scenarioResult() {
            return this.selectedScenario ? this.selectedScenario.result_snapshot || null : null;
        },

        scenarioBlockingIssues() {
            return this.credits.reduce((result, item, index) => {
                if (Number(item.disregarded) === 1 || item.disregarded === true) return result;

                const issues = [];
                const creditorClass = item.scenario_class !== undefined
                    ? item.scenario_class
                    : item.creditor_class;
                if (creditorClass === null || creditorClass === undefined || creditorClass === "") {
                    issues.push("informe a classe");
                }
                if (Number(item.currency || 1) !== 1) {
                    if (!this.effectiveExchangeRate(item)) issues.push("informe a cotação da moeda estrangeira");
                    if (!this.effectiveExchangeRateDate(item)) issues.push("informe a data-base da cotação");
                }

                if (issues.length) {
                    const references = [item.registration, item.credit_reference].filter(Boolean).join(" · ");
                    result.push({
                        key: item.id || `${item.registration || "credit"}-${item.credit_reference || index}`,
                        credit: `${item.name || "Crédito sem nome"}${references ? ` (${references})` : ""}`,
                        message: issues.join(" e "),
                    });
                }
                return result;
            }, []);
        },

        canApplyImport() {
            if (!this.importPreview || this.importApplying) return false;
            if (this.importPreview.summary.ambiguous > 0) return false;
            if (this.importPreview.summary.invalid > 0 && !this.discardInvalidImportRows) return false;
            return this.importPreview.summary.valid > 0;
        },

        filteredScenarios() {
            const term = String(this.scenarioSearch || "").trim().toLowerCase();
            if (!term) return this.scenarios;

            return this.scenarios.filter((item) => (
                String(item.name || "").toLowerCase().includes(term)
                || String(item.description || "").toLowerCase().includes(term)
            ));
        },

        filteredCredits() {
            const term = String(this.creditSearch || "").replace(/[./-]/g, "").trim().toLowerCase();
            if (!term) return this.credits;

            return this.credits.filter((item) => {
                const haystack = [
                    item.name,
                    item.registration,
                    item.credit_reference,
                ].join(" ").replace(/[./-]/g, "").toLowerCase();

                return haystack.includes(term);
            });
        },

        changes() {
            const rows = [];

            this.credits.forEach((item) => {
                const common = {
                    creditor: item.name,
                    reference: item.credit_reference || "—",
                    reason: item.change_reason || "Não informado",
                    source: item,
                };

                if (this.hasClassChange(item)) {
                    rows.push({
                        ...common,
                        type: "Classe",
                        before: this.className(item.base_creditor_class),
                        after: this.className(item.scenario_class),
                    });
                }
                if (this.hasAmountChange(item)) {
                    rows.push({
                        ...common,
                        type: "Valor",
                        before: this.formatAmount(item.base_amount),
                        after: this.formatAmount(item.amount),
                    });
                }
                if (item.disregarded) {
                    rows.push({ ...common, type: "Desconsideração", before: "Considerado", after: "Desconsiderado" });
                } else if (!item.vote_eligible) {
                    rows.push({ ...common, type: "Direito de voto", before: "Com direito", after: "Sem direito" });
                }
                if (Number(item.origin) === 2) {
                    rows.push({ ...common, type: "Inclusão manual", before: "Não constava", after: this.formatAmount(item.amount) });
                }
            });

            return rows;
        },

        statusSummary() {
            const counts = this.scenarios.reduce((result, item) => {
                const name = this.statusName(item.status);
                result[name] = (result[name] || 0) + 1;
                return result;
            }, {});

            return Object.keys(counts).map((key) => `${counts[key]} ${key.toLowerCase()}`).join(" · ") || "Nenhum cenário";
        },

    },

    watch: {
        activeTab(value) {
            if (value === 6 && this.selectedScenarioId) this.loadAudit();
        },
    },

    created() {
        this.selectedVotingId = this.$route.query.voting_id
            ? Number(this.$route.query.voting_id)
            : null;
        this.loadAll();
    },

    methods: {
        backToScenarioList() {
            this.activeTab = 0;
        },

        meetingExchangeRate(currency) {
            if (Number(currency || 1) === 1) return 1;
            if (Number(currency) === 2) return Number(this.meeting.exchange_rate_usd || 0) || null;
            if (Number(currency) === 3) return Number(this.meeting.exchange_rate_eur || 0) || null;
            return null;
        },

        effectiveExchangeRate(item) {
            return Number(item.exchange_rate || 0) || this.meetingExchangeRate(item.currency);
        },

        effectiveExchangeRateDate(item) {
            return item.exchange_rate_date || this.meeting.exchange_rate_date || null;
        },

        async exportCaseQgc() {
            this.exportLoading = true;
            try {
                const response = await axios.get(
                    `/api/admin/general-meeting-votings/scenario-qgc-export/${this.meetingId}`,
                    { responseType: "blob" }
                );
                const disposition = response.headers["content-disposition"] || "";
                const match = disposition.match(/filename="?([^";]+)"?/i);
                const fileName = match ? match[1] : `qgc-caso-${this.meetingId}.xlsx`;
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.$dialog.notify.success("QGC do caso exportado. A planilha já está pronta para ajustes e reimportação no cenário.", {
                    position: "top-right",
                    timeout: 3500,
                });
            } catch (error) {
                await this.notifyExportError(error);
            } finally {
                this.exportLoading = false;
            }
        },

        downloadExport(response, fallbackName) {
            const disposition = response.headers["content-disposition"] || "";
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const fileName = match ? match[1] : fallbackName;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },

        async exportSelectedScenario() {
            if (!this.selectedScenario) return;
            this.scenarioExportLoading = true;
            try {
                const response = await axios.get(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/export`,
                    { responseType: "blob", params: this.selectedVotingId ? { voting_id: this.selectedVotingId } : {} }
                );
                this.downloadExport(response, `cenario-${this.selectedScenario.id}.xlsx`);
                this.$dialog.notify.success("Cenário exportado com composição, alterações e memória do resultado.", {
                    position: "top-right",
                    timeout: 3000,
                });
            } catch (error) {
                await this.notifyExportError(error);
            } finally {
                this.scenarioExportLoading = false;
            }
        },

        async exportScenarioBallot() {
            if (!this.selectedScenario || !this.selectedVotingId) return;
            this.ballotExportLoading = true;
            try {
                const response = await axios.get(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/ballot/export`,
                    { responseType: "blob", params: { general_meeting_voting_id: this.selectedVotingId } }
                );
                this.downloadExport(response, `cedula-votacao-${this.selectedVotingId}-cenario-${this.selectedScenario.id}.xlsx`);
            } catch (error) {
                await this.notifyExportError(error);
            } finally {
                this.ballotExportLoading = false;
            }
        },

        async exportScenarioResult() {
            if (!this.selectedScenario || !this.scenarioResult) return;
            this.resultExportLoading = true;
            try {
                const response = await axios.get(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/result/export`,
                    { responseType: "blob", params: this.selectedVotingId ? { voting_id: this.selectedVotingId } : {} }
                );
                this.downloadExport(response, `resultado-cenario-${this.selectedScenario.id}.pdf`);
                this.$dialog.notify.success("Relatório do resultado exportado.", {
                    position: "top-right",
                    timeout: 3000,
                });
            } catch (error) {
                await this.notifyExportError(error);
            } finally {
                this.resultExportLoading = false;
            }
        },

        async loadAll() {
            this.loading = true;

            try {
                const [meetingResult, votingResult] = await Promise.all([
                    axios.get(`/api/admin/general-meetings/${this.meetingId}`),
                    axios.get(`/api/admin/general-meeting-votings/action/${this.meetingId}`),
                ]);

                this.meeting = meetingResult.data.content || {};
                this.votings = votingResult.data.content || [];

                // Classes do caso da assembleia. Inclui ocultas para que
                // participantes ja cadastrados mantenham o nome da classe.
                this.creditorTypes = await creditorClassUtil.listLabels(this.meeting.client_id);

                if (this.selectedVotingId && !this.votings.some((item) => Number(item.id) === Number(this.selectedVotingId))) {
                    this.selectedVotingId = null;
                }
                if (!this.selectedVotingId && this.votings.length === 1) {
                    this.selectedVotingId = this.votings[0].id;
                }

                await this.loadScenarios();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        async loadScenarios() {
            const result = await axios.get(`/api/admin/general-meeting-votings/scenarios/${this.meetingId}`, {
                params: this.selectedVotingId ? { voting_id: this.selectedVotingId } : {},
            });

            this.scenarios = result.data.content || [];
            this.comparison.selected = this.scenarios.filter((item) => item.linked_to_voting).map((item) => item.id);
            this.comparison.errors = {};

            if (!this.scenarios.some((item) => Number(item.id) === Number(this.selectedScenarioId))) {
                const official = this.scenarios.find((item) => Number(item.status) === STATUSES.OFFICIAL);
                this.selectedScenarioId = official ? official.id : (this.scenarios[0] ? this.scenarios[0].id : null);
            }

            if (this.selectedScenarioId) await this.selectScenario(this.selectedScenarioId, false);
        },

        async onVotingChange() {
            this.selectedScenarioId = null;
            await this.loadScenarios();
        },

        currentVoting() {
            return this.votings.find((item) => Number(item.id) === Number(this.selectedVotingId)) || null;
        },

        isBaseScenario(item) {
            const voting = this.currentVoting();
            return !!voting && Number(voting.scenario_id) === Number(item.id);
        },

        toggleComparison(id, checked) {
            const selected = this.comparison.selected.filter((item) => item !== id);
            this.comparison.selected = checked ? [...selected, id] : selected;
        },

        comparisonResult(item) {
            const result = item.voting_result;
            return result && result.ordinary ? result : null;
        },

        comparisonSummary(item) {
            const result = this.comparisonResult(item);
            return (result && result.ordinary && result.ordinary.summary) || {};
        },

        comparisonVerdict(item) {
            const result = this.comparisonResult(item);
            const ordinary = result.ordinary || {};
            const summary = ordinary.summary || {};
            if (Number(ordinary.voting_type) === 1 && summary.tie) return { text: "Aprovado (empate)", tone: "warning" };
            if (!ordinary.has_effective_votes) return { text: "Sem votos", tone: "neutral" };
            return ordinary.approved ? { text: "Aprovado", tone: "success" } : { text: "Rejeitado", tone: "danger" };
        },

        async openScenarioResult(item) {
            await this.selectScenario(item.id, false);
            this.activeTab = 3;
        },

        // Apura de uma vez os cenarios marcados com os votos da votacao em contexto.
        async calculateComparison() {
            if (!this.selectedVotingId) return;
            this.comparison.loading = true;
            try {
                const result = await axios.post(`/api/admin/general-meeting-votings/${this.selectedVotingId}/scenario-results`, {
                    scenario_ids: this.comparison.selected,
                });
                const rows = (result.data.content && result.data.content.results) || [];
                const errors = {};
                rows.filter((row) => row.error).forEach((row) => { errors[row.scenario_id] = row.error; });
                await this.loadScenarios();
                this.comparison.errors = errors;
                const failed = Object.keys(errors).length;
                if (failed) {
                    this.$dialog.notify.warning(`${rows.length - failed} cenário(s) apurado(s); ${failed} com pendência (veja na tabela).`, { position: "top-right", timeout: 4000 });
                } else {
                    this.$dialog.notify.info(`${rows.length} cenário(s) apurado(s) com os votos desta votação.`, { position: "top-right", timeout: 2500 });
                }
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.comparison.loading = false;
            }
        },

        // Clique na linha = acao "Abrir cenario".
        openScenarioRow(item) {
            this.selectScenario(item.id);
        },

        async selectScenario(id, switchTab = true) {
            this.selectedScenarioId = id || null;
            this.credits = [];
            this.auditLogs = [];

            if (!this.selectedScenarioId) return;

            await Promise.all([this.loadScenarioCredits(), this.loadScenarioDetails()]);
            if (switchTab) this.activeTab = 1;
        },

        async loadScenarioDetails() {
            // Com votacao em contexto, o resultado vem apurado com os votos dela.
            const result = await axios.get(`/api/admin/general-meeting-votings/scenario/${this.selectedScenarioId}`, {
                params: this.selectedVotingId ? { voting_id: this.selectedVotingId } : {},
            });
            const details = result.data.content || {};
            const index = this.scenarios.findIndex((item) => Number(item.id) === Number(details.id));

            if (index >= 0) this.$set(this.scenarios, index, { ...this.scenarios[index], ...details });
            if (!this.selectedVotingId && details.general_meeting_voting_id) {
                this.selectedVotingId = details.general_meeting_voting_id;
            }
        },

        async loadScenarioCredits() {
            this.creditsLoading = true;

            try {
                const result = await axios.get(`/api/admin/general-meeting-votings/scenario-creditors/${this.meetingId}`, {
                    params: { scenario_id: this.selectedScenarioId },
                });

                this.credits = (result.data.content || []).map((item) => ({
                    ...item,
                    scenario_class: item.scenario_participant_id
                        && (item.configured_scenario_class === null || item.configured_scenario_class === undefined || item.configured_scenario_class === "")
                        ? null
                        : (item.scenario_class || item.type || null),
                    base_creditor_class: item.base_creditor_class || item.type || null,
                    amount: Number(item.amount || 0),
                    base_amount: Number(item.base_amount !== null && item.base_amount !== undefined ? item.base_amount : item.amount || 0),
                    currency: item.currency || 1,
                    base_currency: item.base_currency || item.currency || 1,
                    exchange_rate: item.exchange_rate || this.meetingExchangeRate(item.currency),
                    exchange_rate_date: item.exchange_rate_date || this.meeting.exchange_rate_date || null,
                    vote_eligible: Number(item.vote_eligible) !== 0 && Number(item.blocked) !== 1,
                    disregarded: Number(item.disregarded) === 1,
                }));
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.creditsLoading = false;
            }
        },

        async loadAudit() {
            try {
                const result = await axios.get(`/api/admin/general-meeting-votings/scenario/${this.selectedScenarioId}/audit`);
                this.auditLogs = result.data.content || [];
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadDetailDocuments(credit) {
            this.detailDocuments = [];
            if (!this.selectedScenario || !credit) return;
            try {
                const result = await axios.get(`/api/admin/general-meeting-scenarios/${this.selectedScenario.id}/documents`, { params: { credit_reference: credit.credit_reference } });
                this.detailDocuments = result.data.content || [];
            } catch (error) { this.notifyError(error); }
        },

        async openCreditDetails(item) { this.detailCredit = item; await this.loadDetailDocuments(item); this.creditDetailDialog = true; },
        async openChangeDetails(item) { this.detailChange = item; await this.loadDetailDocuments(item.source); this.changeDetailDialog = true; },
        async downloadScenarioDocument(item) {
            try {
                const response = await axios.get(`/api/admin/general-meeting-scenario-documents/${item.id}/download`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a"); link.href = url; link.download = item.original_name || item.name || "documento";
                document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
            } catch (error) { this.notifyError(error); }
        },

        async calculateResult() {
            if (!this.selectedScenario || !this.selectedVotingId) return;

            this.resultLoading = true;
            try {
                const result = await axios.post(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/calculate`,
                    { general_meeting_voting_id: this.selectedVotingId }
                );
                const index = this.scenarios.findIndex((item) => Number(item.id) === Number(this.selectedScenario.id));
                if (index >= 0) {
                    // O cenario e da AGC: nao passa a pertencer a votacao apurada.
                    this.$set(this.scenarios, index, {
                        ...this.scenarios[index],
                        result_snapshot: result.data.content,
                        voting_result: result.data.content,
                        voting_result_at: new Date().toISOString(),
                        linked_to_voting: true,
                    });
                    if (!this.comparison.selected.includes(this.selectedScenario.id)) this.comparison.selected.push(this.selectedScenario.id);
                }
                this.$dialog.notify.info("Resultado do cenário apurado.", {
                    position: "top-right",
                    timeout: 2500,
                });
                await this.loadAudit();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.resultLoading = false;
            }
        },

        startScenarioImport() {
            this.importPreview = null;
            this.discardInvalidImportRows = false;
            this.importMode = "differences";

            if (!this.selectedScenario) {
                this.openNewScenario("spreadsheet", true);
                return;
            }
            if (!this.selectedScenario.is_editable) {
                this.importDraftTargetId = this.editableScenarios.length === 1
                    ? this.editableScenarios[0].id
                    : null;
                this.importTargetDialog = true;
                return;
            }

            this.pickSpreadsheetNow();
        },

        openImportPicker() {
            this.importPickerDialog = true;
        },

        // Abre o seletor de arquivo no proprio clique do botao (gesto do usuario).
        pickSpreadsheetNow() {
            const input = this.$refs.qgcFileInput;
            if (input) {
                input.value = "";
                input.click();
            }
            this.importPickerDialog = false;
        },

        triggerSpreadsheetInput() {
            this.$nextTick(() => {
                if (!this.$refs.qgcFileInput) return;
                this.$refs.qgcFileInput.value = "";
                this.$refs.qgcFileInput.click();
            });
        },

        async onScenarioSpreadsheetSelected(event) {
            const file = event.target.files && event.target.files[0];
            if (!file || !this.selectedScenario) return;

            this.importLoading = true;
            try {
                const formData = new FormData();
                formData.append("file", file);
                const result = await axios.post(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/import/preview`,
                    formData
                );
                this.importPreview = result.data.content;
                this.discardInvalidImportRows = false;
                this.importMode = "differences";
                this.importPreviewDialog = true;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.importLoading = false;
                event.target.value = "";
            }
        },

        async duplicateSelectedForImport() {
            if (!this.selectedScenario) return;

            this.importLoading = true;
            try {
                const result = await axios.post(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/duplicate`,
                    {}
                );
                this.selectedScenarioId = result.data.content.id;
                this.importTargetDialog = false;
                await this.loadScenarios();
                this.openImportPicker();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.importLoading = false;
            }
        },

        openNewScenarioForImport() {
            this.importTargetDialog = false;
            this.openNewScenario("spreadsheet", true);
        },

        async selectDraftForImport() {
            if (!this.importDraftTargetId) return;
            this.selectedScenarioId = this.importDraftTargetId;
            this.importTargetDialog = false;
            await this.selectScenario(this.importDraftTargetId, false);
            this.openImportPicker();
        },

        closeImportPreview() {
            if (this.importApplying) return;
            this.importPreviewDialog = false;
            this.importPreview = null;
        },

        resolveImportAmbiguity(row, participantId) {
            const candidate = (row.candidates || []).find((item) => (
                Number(item.participant_id) === Number(participantId)
            ));
            if (!candidate) return;

            row.matched_participant_id = candidate.participant_id;
            row.current = {
                name: candidate.name,
                credit_reference: candidate.credit_reference,
                creditor_class: candidate.creditor_class,
                amount: candidate.amount,
                currency: candidate.currency,
                vote_eligible: candidate.vote_eligible,
                disregarded: candidate.disregarded,
                client_company_id: candidate.client_company_id || null,
                client_company_name: candidate.client_company_name || null,
            };
            row.proposed = {
                ...row.proposed,
                name: row.proposed.name || candidate.name,
                email: row.proposed.email || candidate.email,
                registration: row.proposed.registration || candidate.registration,
                credit_reference: row.proposed.credit_reference || candidate.credit_reference,
                creditor_class: row.proposed.creditor_class || candidate.creditor_class,
                amount: row.proposed.amount !== null ? row.proposed.amount : candidate.amount,
                currency: row.proposed.currency || candidate.currency,
                client_company_id: candidate.client_company_id || null,
                client_company_name: candidate.client_company_name || null,
            };

            const unchanged = Number(row.current.creditor_class) === Number(row.proposed.creditor_class)
                && Number(row.current.amount) === Number(row.proposed.amount)
                && Number(row.current.currency) === Number(row.proposed.currency)
                && !!row.current.vote_eligible === !!row.proposed.vote_eligible
                && !!row.current.disregarded === !!row.proposed.disregarded;
            row.status = unchanged ? "unchanged" : "changed";
            row.errors = [];
            this.recalculateImportSummary();
        },

        recalculateImportSummary() {
            if (!this.importPreview) return;
            const rows = this.importPreview.rows || [];
            const count = (status) => rows.filter((item) => item.status === status).length;
            this.importPreview.summary = {
                total: rows.length,
                valid: rows.filter((item) => !["invalid", "ambiguous"].includes(item.status)).length,
                unchanged: count("unchanged"),
                changed: count("changed"),
                new: count("new"),
                invalid: count("invalid"),
                ambiguous: count("ambiguous"),
                pending: count("invalid") + count("ambiguous"),
            };
        },

        async applyScenarioImport() {
            if (!this.canApplyImport || !this.selectedScenario) return;

            this.importApplying = true;
            try {
                const result = await axios.post(
                    `/api/admin/general-meeting-votings/scenario/${this.selectedScenario.id}/import/apply`,
                    {
                        rows: this.importPreview.rows,
                        mode: this.importMode,
                        discard_invalid: this.discardInvalidImportRows,
                        file_name: this.importPreview.file_name,
                        qgc_version: this.importPreview.qgc_version,
                    }
                );
                this.importPreviewDialog = false;
                this.importPreview = null;
                await this.loadScenarios();
                this.activeTab = 1;
                const summary = result.data.content;
                this.$dialog.notify.info(
                    `${summary.applied} linhas aplicadas ao cenário; ${summary.discarded} descartadas.`,
                    { position: "top-right", timeout: 3500 }
                );
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.importApplying = false;
            }
        },

        openNewScenario(sourceType = "qgc", importAfterCreate = false) {
            this.pendingImportAfterCreate = importAfterCreate;
            this.scenarioForm = {
                name: "",
                description: "",
                legal_basis: "",
                internal_notes: "",
                source_type: sourceType,
            };
            this.scenarioDialog = true;
        },

        closeScenarioDialog() {
            if (!this.saving) {
                this.pendingImportAfterCreate = false;
                this.scenarioDialog = false;
            }
        },

        async createScenario() {
            if (!String(this.scenarioForm.name || "").trim()) {
                this.$dialog.notify.error("Informe o nome do cenário.", { position: "top-right", timeout: 2500 });
                return;
            }

            this.saving = true;

            try {
                const result = await axios.post("/api/admin/general-meeting-votings/scenarios", {
                    ...this.scenarioForm,
                    general_meeting_id: this.meetingId,
                });

                this.scenarioDialog = false;
                this.selectedScenarioId = result.data.content.id;
                await this.loadScenarios();
                this.activeTab = 1;
                this.$dialog.notify.info("Cenário criado como rascunho.", { position: "top-right", timeout: 2500 });
                if (this.pendingImportAfterCreate) {
                    this.pendingImportAfterCreate = false;
                    this.openImportPicker();
                }
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.saving = false;
            }
        },

        async duplicateScenario(item) {
            try {
                const result = await axios.post(`/api/admin/general-meeting-votings/scenario/${item.id}/duplicate`, {});
                this.selectedScenarioId = result.data.content.id;
                await this.loadScenarios();
                this.activeTab = 1;
                this.$dialog.notify.info("Cenário duplicado como rascunho.", { position: "top-right", timeout: 2500 });
            } catch (error) {
                this.notifyError(error);
            }
        },

        async advanceScenario(item) {
            const next = {
                [STATUSES.DRAFT]: STATUSES.READY,
                [STATUSES.READY]: STATUSES.FINALIZED,
                [STATUSES.FINALIZED]: STATUSES.OFFICIAL,
                [STATUSES.ARCHIVED]: STATUSES.DRAFT,
            }[Number(item.status)];

            if (!next || !this.canAdvance(item)) return;

            try {
                await axios.patch(`/api/admin/general-meeting-votings/scenario/${item.id}/status`, { status: next });
                await this.loadScenarios();
                this.$dialog.notify.info("Status do cenário atualizado.", { position: "top-right", timeout: 2500 });
            } catch (error) {
                this.notifyError(error);
            }
        },

        async deleteScenario(item) {
            if (!this.canDelete(item)) return;

            const confirmed = await this.$dialog.confirm({
                title: "Excluir cenário",
                text: "Deseja excluir este cenário? Esta operação não poderá ser desfeita.",
                actions: { false: "Cancelar", true: "Excluir" },
            });
            if (!confirmed) return;

            try {
                await axios.delete(`/api/admin/general-meeting-votings/scenarios/${item.id}`);
                if (Number(this.selectedScenarioId) === Number(item.id)) this.selectedScenarioId = null;
                await this.loadScenarios();
                this.$dialog.notify.info("Cenário excluído.", { position: "top-right", timeout: 2500 });
            } catch (error) {
                this.notifyError(error);
            }
        },

        addManualCredit() {
            if (!this.selectedScenario || !this.selectedScenario.is_editable) return;

            this.credits.unshift({
                local_id: `manual-${Date.now()}`,
                name: "",
                email: "",
                registration: "",
                scenario_class: null,
                base_creditor_class: null,
                amount: 0,
                base_amount: 0,
                currency: 1,
                base_currency: 1,
                vote_eligible: true,
                disregarded: false,
                change_reason: "",
                origin: 2,
                has_presence: true,
                qgc_creditor_id: null,
                creditor_search: "",
            });
        },

        qgcCreditorLabel(item) {
            if (!item) return "";
            const company = item.client_company_name ? ` · ${item.client_company_name}` : "";
            return `${item.name} · ${item.registration || "sem CPF/CNPJ"}${company}`;
        },

        searchQgcCreditors(item, value) {
            const search = String(value || "").trim();
            if (!item.qgc_creditor_id) item.name = search;

            clearTimeout(this.qgcSearchTimer);
            if (search.length < 2) {
                this.qgcCreditorOptions = [];
                return;
            }

            this.qgcSearchTimer = setTimeout(async () => {
                this.qgcSearchLoading = true;
                try {
                    const result = await axios.get(
                        `/api/admin/general-meeting-votings/scenario-qgc-creditors/${this.meetingId}`,
                        { params: { search, version: this.selectedScenario ? this.selectedScenario.qgc_version : null } }
                    );
                    this.qgcCreditorOptions = result.data.content || [];
                } catch (error) {
                    this.notifyError(error);
                } finally {
                    this.qgcSearchLoading = false;
                }
            }, 300);
        },

        applyQgcCreditor(item, creditorId) {
            const creditor = this.qgcCreditorOptions.find((option) => Number(option.id) === Number(creditorId));
            if (!creditor) {
                item.user_creditor_id = null;
                item.credit_reference = null;
                item.origin = 2;
                return;
            }

            item.user_creditor_id = creditor.id;
            item.name = creditor.name;
            item.email = creditor.email || "";
            item.registration = creditor.registration || "";
            item.scenario_class = creditor.type || null;
            item.base_creditor_class = creditor.type || null;
            item.amount = Number(creditor.amount || 0);
            item.base_amount = Number(creditor.amount || 0);
            item.currency = creditor.currency || 1;
            item.base_currency = creditor.currency || 1;
            item.credit_reference = `UC-${creditor.id}`;
            item.client_company_id = creditor.client_company_id || null;
            item.client_company_name = creditor.client_company_name || null;
            item.origin = 1;
            this.$nextTick(() => { item.creditor_search = creditor.name; });
        },

        removeCredit(item) {
            const index = this.credits.indexOf(item);
            if (index >= 0) this.credits.splice(index, 1);
        },

        async saveCredits() {
            if (!this.selectedScenario || !this.selectedScenario.is_editable) return;

            const invalid = this.credits.find((item) => (
                !item.name
                || !item.scenario_class
                || (Number(item.origin) === 2 && !String(item.registration || "").replace(/\D/g, ""))
            ));

            if (invalid) {
                this.$dialog.notify.error("Informe credor, CPF/CNPJ e classe para todos os créditos.", {
                    position: "top-right",
                    timeout: 3000,
                });
                return;
            }

            this.saving = true;

            try {
                await axios.post("/api/admin/general-meeting-votings/scenarios", {
                    id: this.selectedScenario.id,
                    name: this.selectedScenario.name,
                    description: this.selectedScenario.description,
                    legal_basis: this.selectedScenario.legal_basis,
                    internal_notes: this.selectedScenario.internal_notes,
                    general_meeting_id: this.meetingId,
                    general_meeting_voting_id: this.selectedScenario.general_meeting_voting_id || null,
                    qgc_version: this.selectedScenario.qgc_version,
                    participants: this.credits.map((item) => ({
                        accreditation_general_meeting_id: item.accreditation_general_meeting_id || null,
                        user_creditor_id: item.user_creditor_id || item.id || null,
                        client_company_id: item.client_company_id || null,
                        user_id: item.user_id || null,
                        name: item.name,
                        email: item.email || null,
                        registration: String(item.registration || "").replace(/\D/g, ""),
                        creditor_class: item.scenario_class,
                        base_creditor_class: item.base_creditor_class,
                        amount: item.amount,
                        base_amount: item.base_amount,
                        currency: item.currency || 1,
                        base_currency: item.base_currency || 1,
                        credit_reference: item.credit_reference || null,
                        vote_eligible: !!item.vote_eligible,
                        disregarded: !!item.disregarded,
                        change_reason: item.change_reason || null,
                        exchange_rate: item.exchange_rate || null,
                        exchange_rate_date: item.exchange_rate_date || null,
                        blocked: !item.vote_eligible,
                        origin: item.origin || 1,
                    })),
                });

                await this.loadScenarios();
                this.$dialog.notify.info("Alterações salvas no rascunho.", { position: "top-right", timeout: 2500 });
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.saving = false;
            }
        },

        canAdvance(item) {
            const status = Number(item.status);
            if (status === STATUSES.OFFICIAL) return false;
            if (status === STATUSES.DRAFT) return Number(item.participant_count || 0) > 0;
            if (status === STATUSES.READY) {
                return Number(item.blocker_count || 0) === 0 && !!item.result_snapshot;
            }
            if (status === STATUSES.FINALIZED) return Number(item.blocker_count || 0) === 0;
            return status === STATUSES.ARCHIVED;
        },

        canDelete(item) {
            return [STATUSES.DRAFT, STATUSES.ARCHIVED].includes(Number(item.status));
        },

        advanceIcon(item) {
            return Number(item.status) === STATUSES.ARCHIVED
                ? "mdi-archive-arrow-up-outline"
                : Number(item.status) === STATUSES.FINALIZED
                    ? "mdi-star-check-outline"
                    : "mdi-arrow-right-circle-outline";
        },

        advanceTooltip(item) {
            const status = Number(item.status);
            if (status === STATUSES.OFFICIAL) return "O cenário oficial está congelado";
            if (status === STATUSES.DRAFT && !this.canAdvance(item)) return "Inclua créditos antes de marcar como pronto";
            if (status === STATUSES.READY && !item.result_snapshot) return "Apure o resultado antes de finalizar o cenário";
            if ([STATUSES.READY, STATUSES.FINALIZED].includes(status) && !this.canAdvance(item)) {
                return `${item.blocker_count || 0} pendência(s) bloqueante(s) impedem esta ação`;
            }
            return {
                [STATUSES.DRAFT]: "Marcar como pronto para apuração",
                [STATUSES.READY]: "Finalizar cenário",
                [STATUSES.FINALIZED]: "Definir como cenário oficial",
                [STATUSES.ARCHIVED]: "Reabrir como rascunho",
            }[status] || "Ação indisponível";
        },

        statusName(status) {
            return {
                [STATUSES.DRAFT]: "Rascunho",
                [STATUSES.READY]: "Pronto para apuração",
                [STATUSES.FINALIZED]: "Finalizado",
                [STATUSES.OFFICIAL]: "Oficial",
                [STATUSES.ARCHIVED]: "Arquivado",
            }[Number(status)] || "Rascunho";
        },

        statusColor(status) {
            return {
                [STATUSES.DRAFT]: "blue lighten-5",
                [STATUSES.READY]: "amber lighten-4",
                [STATUSES.FINALIZED]: "green lighten-4",
                [STATUSES.OFFICIAL]: "primary",
                [STATUSES.ARCHIVED]: "grey lighten-3",
            }[Number(status)] || "grey lighten-3";
        },

        statusTone(status) {
            return {
                [STATUSES.DRAFT]: "info",
                [STATUSES.READY]: "warning",
                [STATUSES.FINALIZED]: "success",
                [STATUSES.OFFICIAL]: "primary",
                [STATUSES.ARCHIVED]: "neutral",
            }[Number(status)] || "neutral";
        },

        statusTextColor(status) {
            return Number(status) === STATUSES.OFFICIAL ? "white" : "grey darken-4";
        },

        className(id) {
            const item = this.creditorTypes.find((type) => Number(type.id) === Number(id));
            return item ? item.name : "Não informada";
        },

        hasClassChange(item) {
            return item.base_creditor_class !== null
                && item.base_creditor_class !== undefined
                && Number(item.base_creditor_class) !== Number(item.scenario_class);
        },

        hasAmountChange(item) {
            return item.base_amount !== null
                && item.base_amount !== undefined
                && Number(item.base_amount) !== Number(item.amount);
        },

        formatAmount(value) {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(value || 0));
        },

        formatPercent(value) {
            return `${Number(value || 0).toFixed(2).replace(".", ",")}%`;
        },

        criterionColor(value) {
            return value ? "success" : "error";
        },

        criterionIcon(value) {
            return value ? "mdi-check-circle-outline" : "mdi-close-circle-outline";
        },

        importStatusName(status) {
            return {
                unchanged: "Sem alteração",
                changed: "Alterado",
                new: "Novo",
                invalid: "Inválido",
                ambiguous: "Ambíguo",
            }[status] || status;
        },

        importStatusTone(status) {
            return {
                unchanged: "neutral",
                changed: "warning",
                new: "primary",
                invalid: "danger",
                ambiguous: "warning",
            }[status] || "neutral";
        },

        importStatusColor(status) {
            return {
                unchanged: "grey",
                changed: "warning",
                new: "primary",
                invalid: "error",
                ambiguous: "deep-orange",
            }[status] || "grey";
        },

        // Carimbos do servidor (UTC) em Brasilia.
        formatDate(value) {
            return value ? this.$options.filters.defaultDate(value, "DD/MM/YYYY HH:mm [BRT]") : "—";
        },

        formatAuditDetails(details) {
            try { return JSON.stringify(typeof details === 'string' ? JSON.parse(details) : details, null, 2); }
            catch (_) { return String(details); }
        },
        auditActionName(action) {
            return {
                "scenario.created": "Cenário criado",
                "scenario.updated": "Cenário atualizado",
                "scenario.duplicated": "Cenário duplicado",
                "scenario.status_changed": "Status alterado",
                "scenario.officialized": "Cenário oficializado",
                "scenario.replaced_as_official": "Substituído como cenário oficial",
                "scenario.result_calculated": "Resultado apurado",
                "scenario.spreadsheet_imported": "Planilha do cenário importada",
            }[action] || action;
        },

        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3500 });
        },

        async notifyExportError(error) {
            let message = null;
            const data = error && error.response ? error.response.data : null;

            if (typeof Blob !== "undefined" && data instanceof Blob) {
                try {
                    const payload = JSON.parse(await data.text());
                    message = payload.message
                        || (payload.error && payload.error.message)
                        || (payload.content && payload.content.message)
                        || null;
                } catch (parseError) {
                    message = null;
                }
            }

            this.$dialog.notify.error(message || this.$codes(error), {
                position: "top-right",
                timeout: 4500,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.metric-card {
    height: 100%;
    padding: 16px;
}

.workspace-card {
    overflow: hidden;
}

.scenario-back-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 48px;
}

.metric-label {
    color: $ui-muted;
    font-size: 12px;
}

.metric-value {
    color: $ui-fg;
    font-size: 24px;
    font-weight: 600;
    margin-top: 4px;
}

.metric-value--text {
    font-size: 14px;
    line-height: 1.35;
}

.scenario-credit-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.scenario-credit-table ::v-deep tbody td {
    vertical-align: top;
    padding-top: 12px;
    padding-bottom: 12px;
}

.scenario-credit-table ::v-deep th:last-child,
.scenario-credit-table ::v-deep td:last-child {
    position: sticky;
    right: 0;
    z-index: 2;
    background: $ui-bg;
}

.scenario-credit-table ::v-deep th:last-child {
    z-index: 3;
    background: $ui-bg-muted;
}

.credit-editor-stack {
    min-width: 180px;
}

.original-value {
    line-height: 18px;
}

.credit-control-center {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    width: 100%;
}

.credit-control-center ::v-deep .v-input {
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
}

.manual-credit-fields {
    display: grid;
    gap: 8px;
    min-width: 300px;
}

.result-snapshot {
    background: $ui-bg-muted;
    border: 1px solid $ui-border;
    border-radius: $ui-radius-sm;
    padding: 16px;
    white-space: pre-wrap;
}

.result-metric {
    height: 100%;
}

.cram-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

@media (max-width: 760px) {
    .cram-grid {
        grid-template-columns: 1fr;
    }
}

.scenario-help-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 8px 24px;
}
</style>
